import { Component, ErrorInfo, ReactNode } from 'react';

import { reportError } from '../reporting/reporter';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, {
      tags: { source: 'react-error-boundary' },
      extra: { componentStack: info.componentStack },
    });
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        style={{
          padding: '60px 20px',
          textAlign: 'center',
          color: 'var(--color-white)',
        }}
      >
        <h2 style={{ marginBottom: 12 }}>Something went wrong</h2>
        <p style={{ color: 'var(--color-medium-gray-text)' }}>
          The app hit an unexpected error. Try reloading the page.
        </p>
        {import.meta.env.DEV && this.state.error && (
          <pre
            style={{
              marginTop: 16,
              padding: 12,
              maxWidth: 560,
              marginLeft: 'auto',
              marginRight: 'auto',
              textAlign: 'left',
              fontSize: 12,
              color: '#fca5a5',
              background: 'rgba(0,0,0,0.35)',
              borderRadius: 8,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {this.state.error.message}
          </pre>
        )}
        <button
          type="button"
          onClick={this.reset}
          style={{
            marginTop: 24,
            padding: '10px 20px',
            backgroundColor: 'var(--color-primary-blue)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}
